import { Box, TextField } from '@mui/material';
import {
  BaseWidget,
  BaseWidgetProps,
  Config,
  FactoryWithContext,
  MultiSelectFieldSettings,
} from '@react-awesome-query-builder/mui';
import { useContext, useState } from 'react';

import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import { MappingError } from '@/pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/FilterMappers';
import { DEFAULT_PLACEHOLDER_FIELD_LABEL } from '@/pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/util';
import { MOUSE_EVENT_BUTTONS } from './const';
import { PLACEHOLDER_BLUE } from './templateCapableComponents/colours';
import TemplatePlaceholderContextMenu, {
  CloseOutcome,
  ContextMenuState,
  handleOpenPlaceholderContextMenu,
} from './templateCapableComponents/TemplatePlaceholderContextMenu';
import { createTemplatePlaceholder } from './util';

const validateIds = (value: string): MappingError[] => {
  const valueArray = value.split(',') ?? [];

  const trimmedArray = valueArray.map((n) => n.trim());

  const isInvalidInput = trimmedArray.some(
    (n) => (n.length !== 11 && n.length !== 0) || n.match(/[^\d]/g)
  );

  return isInvalidInput ? [{ field: 'ids', message: 'Invalid input', level: 'error' }] : [];
};

type IdArrayValue = { value: string | TemplatePlaceholder; errors: MappingError[] };

type IdArrayWidgetProps = {
  asyncFetch: (lookupValue: string) => Promise<string>;
  fetchLabel: string;
  preLoadedOptions?: string;
  templateBuilderDisplayName?: string;
};

type CombinedProps = BaseWidgetProps<Config, IdArrayValue> & IdArrayWidgetProps;

const IdArrayFactory: FactoryWithContext<CombinedProps> = ({
  value,
  setValue,
  fetchLabel,
  templateBuilderDisplayName,
}: CombinedProps) => {
  const [placeholderMenuState, setPlaceholderMenuState] = useState<ContextMenuState>(null);

  const isError = (value?.errors.length ?? 0) > 0;

  const inSearchTemplate = useContext(SearchTemplatePageContext);

  const isPlaceHolder = isTemplatePlaceholder(value?.value);

  const placeholderStyle = isPlaceHolder
    ? {
        sx: {
          '& .MuiFilledInput-root.Mui-focused': {
            backgroundColor: PLACEHOLDER_BLUE,
            width: '544px',
          },
          '& .MuiOutlinedInput-root.Mui-focused': {
            backgroundColor: PLACEHOLDER_BLUE,
            width: '544px',
          },
          caretColor: 'transparent',
        },
        focused: true,
        slotProps: {
          htmlInput: {
            readOnly: true,
            onMouseDown: (e: { button: number; preventDefault: () => void }) => {
              // Only prevent default if it's a left click (0)
              // This keeps Right Click (2) working for the context menu
              if (e.button === 0) {
                e.preventDefault();
              }
            },
          },
        },
      }
    : {};

  const handleCloseTemplatePlaceholderContextMenu = (reason: CloseOutcome) => {
    setPlaceholderMenuState(null);
    if (reason === 'setIsTemplatePlaceholder') {
      setValue({
        value: createTemplatePlaceholder(templateBuilderDisplayName || ''),
        errors: [],
      });
    } else if (reason === 'unsetIsTemplatePlaceholder') {
      setValue({ value: '', errors: [] });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: "0.5rem",
        flexDirection: 'column'
      }}>
      <TextField
        label={'Add 11 digit SA1(s)'}
        variant="filled"
        sx={{ minWidth: '70ch', maxWidth: '70ch' }}
        key={fetchLabel}
        onChange={(e) => {
          if (isPlaceHolder === false) {
            const correctedString = e.target.value.replace(/[^0-9,\s]/, '');

            setValue({ value: correctedString, errors: validateIds(correctedString) });
          }
        }}
        value={isTemplatePlaceholder(value?.value) ? '' : (value?.value ?? '')}
        error={isError}
        helperText={
          isError
            ? 'Please enter only 11 digit numbers and use commas to separate multiple listed numbers'
            : undefined
        }
        onMouseDownCapture={(event) => {
          if (event.button === MOUSE_EVENT_BUTTONS.right && inSearchTemplate) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        onContextMenuCapture={(event) => {
          if (inSearchTemplate) {
            event.preventDefault();
            event.stopPropagation();
          }
          if (inSearchTemplate) {
            handleOpenPlaceholderContextMenu(event, placeholderMenuState, setPlaceholderMenuState);
          }
        }}
        placeholder={isPlaceHolder === true ? DEFAULT_PLACEHOLDER_FIELD_LABEL : undefined}
        color={isPlaceHolder === true ? 'info' : undefined}
        {...placeholderStyle}
      />
      <TemplatePlaceholderContextMenu
        menuState={placeholderMenuState}
        placeholderActive={isPlaceHolder}
        onClose={handleCloseTemplatePlaceholderContextMenu}
      />
    </Box>
  );
};

export const idArrayWidget: BaseWidget & MultiSelectFieldSettings<string> = {
  type: 'idArray',
  hideOperator: true,
  operatorInlineLabel: 'is one of',
  asyncFetch: true,

  useAsyncSearch: true,
  showSearch: true,
  allowCustomValues: true,
  factory: IdArrayFactory,
};
